import { FunctionComponent, useState, useCallback, useEffect, ReactNode, memo } from "react";

import Backdrop from "@mui/material/Backdrop";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import { styled, Theme } from "@mui/material/styles";
import classNames from "classnames";

import { DashboardIcon } from "@/dashboard-design-system/Icon/DashboardIcon";
import { Cross as CloseIcon } from "@/icons/outlined";

const PREFIX = "DashboardMobilePopupDisplay";

const classes = {
  container: `${PREFIX}-container`,
  open: `${PREFIX}-open`,
  header: `${PREFIX}-header`,
  icon: `${PREFIX}-icon`,
  closeIcon: `${PREFIX}-closeIcon`,
  textSecondary: `${PREFIX}-textSecondary`,
  textWhite: `${PREFIX}-textWhite`,
  content: `${PREFIX}-content`,
  backdrop: `${PREFIX}-backdrop`,
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = memo(
  styled("div")(({ theme }: { theme: Theme }) => ({
    [`&& .${classes.container}`]: {
      position: "fixed",
      bottom: 0,
      left: 0,
      right: 0,
      backgroundColor: theme.palette.primary.main,
      borderRadius: `${theme.radius(5)} ${theme.radius(5)} 0px 0px`,
      zIndex: theme.zIndex.hubspot + 2,
      display: "none",
      height: 0,
      transition: "height 300ms",
    },

    [`&& .${classes.open}`]: {
      display: "block",
      height: 260,
    },

    [`&& .${classes.header}`]: {
      padding: theme.spacing(1, 0),
    },

    [`&& .${classes.icon}`]: {
      margin: theme.spacing(1),
    },

    [`&& .${classes.closeIcon}`]: {
      fill: "white",
    },

    [`&& .${classes.textSecondary}`]: {
      color: theme.palette.secondary.main,
    },

    [`&& .${classes.textWhite}`]: {
      color: theme.palette.neutralVariant[100],
    },

    [`&& .${classes.content}`]: {
      textAlign: "center",
    },

    [`&& .${classes.backdrop}`]: {
      zIndex: theme.zIndex.hubspot + 1,
    },
  }))
);

export interface DashboardMobilePopupDisplayProps {
  popupContent: ReactNode;
  onClose?: () => void;
}

export const DashboardMobilePopupDisplay: FunctionComponent<
  React.PropsWithChildren<DashboardMobilePopupDisplayProps>
> = ({ children, popupContent, onClose }) => {
  const [showPopup, setShowPopup] = useState(true);

  const closePopup = useCallback(() => {
    setShowPopup(false);
    onClose?.();
  }, [onClose]);

  useEffect(() => {
    // Forbid the scroll on the page when the warning is visible
    if (showPopup) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "visible";
    }
  }, [showPopup]);

  return (
    <Root>
      <Backdrop className={classes.backdrop} open={showPopup} onClick={closePopup} />
      <Container className={classNames(classes.container, { [classes.open]: showPopup })}>
        <Grid container justifyContent="flex-end" className={classes.header}>
          <Grid item>
            <DashboardIcon size="large" onClick={closePopup} className={classes.icon}>
              <CloseIcon className={classes.closeIcon} />
            </DashboardIcon>
          </Grid>
        </Grid>
        <Grid container className={classes.content} spacing={2}>
          {popupContent}
        </Grid>
      </Container>
      {children}
    </Root>
  );
};
